import { useEffect, useState } from "react";
import { AiOutlineMinusCircle, AiOutlinePlusCircle } from "react-icons/ai";
import API from "../../../utils/API";
import Input from "../../../utils/components/Input";
import Range from "../../../utils/components/Range";
import Rating from "../../../utils/components/Rating";
import SelectBox from "../../../utils/components/SelectBox";
import Textarea from "../../../utils/components/Textarea";
import { useCan } from "../../../utils/hooks/useCan";
import { useProject } from "../../../utils/hooks/useProject";
import ButtonLayout from "./ButtonLayout";
import CheckboxLayout from "./CheckboxLayout";
import DateTimeLayout from "./DateTimeLayout";
import FormFieldActions from "./FormFieldActions";
import GPSMap from "./GPSMap";
import Increment from "./Increment";
import LatLngMap from "./LatLngMap";
import Photos from "./Photos";
import RadioLayout from "./RadioLayout";
import SignaturePad from "./SignaturePad";
import { TEXTTYPE } from "../../../utils/db/constants";
import {
  findFieldIndices,
  getCascadingFields,
  getCascadingOptions,
  getDynamicPlaceholder,
  getFieldValue,
  isCascadingFieldEnabled,
} from "../../../utils/helpers/cascadingFieldHelpers";

const Forms = ({
  forms,
  handleAddRemoveRepeatableForm,
  handleUpdateFormField,
  handleUpdateFormFieldAttachment,
  location,
  language,
  translate,
  isEditing = true,
  handleKeepOldForm,
  userLocation,
}) => {
  const [formTemplates, setFormTemplates] = useState([]);
  const { project } = useProject();

  const can = useCan();

  useEffect(() => {
    const site_set_id = location?.length ? location[0].id : location?.set_id;

    if (!can.doThat("form:update") && !can.doThat("form:create")) return false;

    if (site_set_id)
      if (project)
        API.getFormTemplates(project?.id, 1, 1000, null, true, {
          attributes: [],
          site_set_id,
          site_set_level: location.length - 1 || 1,
        }).then(({ data: res }) => {
          setFormTemplates(res.data);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, location]);

  const handleCascadingChange = (selectedValue, formIndex, fieldIndex, data, repeatableIndex = null, repeatableFieldIndex = null) => {
    const currentValue = getFieldValue({ forms, formIndex, fieldCode: data.field_code, repeatableIndex });

    const getCurrentNormalizedValue = () => {
      if (!currentValue) return null;
      return typeof currentValue === "object" ? currentValue.value : currentValue;
    };

    const getSelectedNormalizedValue = () => {
      if (!selectedValue) return null;
      return typeof selectedValue === "object" ? selectedValue.value : selectedValue;
    };

    handleUpdateFormField(selectedValue, formIndex, fieldIndex, repeatableIndex, repeatableFieldIndex);

    if (getCurrentNormalizedValue() === getSelectedNormalizedValue()) return;

    clearChildFields(formIndex, data.field_code, repeatableIndex);
  };

  const clearChildFields = (formIndex, parentFieldCode, repeatableIndex = null) => {
    const cascadingFields = getCascadingFields({ forms, formIndex, repeatableIndex });

    const childFields = cascadingFields.filter(field =>
      field.cascading_main_field_code === parentFieldCode,
    );

    childFields.forEach(childField => {
      const indices = findFieldIndices({ forms, formIndex, fieldCode: childField.field_code, repeatableIndex });
      if (!indices) return;
      if (repeatableIndex !== null && indices.repeatableFieldIndex === null) return;

      handleUpdateFormField("", formIndex, indices.fieldIndex, repeatableIndex, indices.repeatableFieldIndex);
      clearChildFields(formIndex, childField.field_code, repeatableIndex);
    });
  };

  const componentRender = (
    data,
    formIndex,
    fieldIndex,
    repeatableIndex = null,
    repeatableFieldIndex = null,
  ) => {
    switch (data.field_type) {
      case "text":
        return (
          <Input
            value={data.value}
            maxLength={data.max_characters || 500}
            onChange={(e) =>
              handleUpdateFormField(
                e.target.value,
                formIndex,
                fieldIndex,
                repeatableIndex,
                repeatableFieldIndex,
              )
            }
            disabled={data.read_only || !isEditing || forms[formIndex].isLocked}
            isEditor={data.type === TEXTTYPE.EDITOR}
          />
        );
      case "title":
        return <h4>{data.field_label}</h4>;
      case "link":
        return (
          <a
            href={data.url}
            className={
              data.style === "button"
                ? `btn btn-orange border rounded shadow-sm text-white`
                : `text-decoration-underline text-orange`
            }
            target="_blank"
            rel="noreferrer noopener"
          >
            {data.style === "button" ? data.button_label : data.url}
          </a>
        );
      case "number":
        return (
          <div className="d-flex flex-column justify-content-center align-items-center w-100">
            <div className="d-flex justify-content-center align-items-center w-100">
              {data.style === "currency" && data.currency ? (
                <span className="me-2 fs-4">{data.currency}</span>
              ) : null}
              <Input
                type="number"
                value={data.value || data.default_value}
                onChange={(e) => {
                  const newValue = e.target.value;
                  handleUpdateFormField(
                    newValue,
                    formIndex,
                    fieldIndex,
                    repeatableIndex,
                    repeatableFieldIndex,
                  );
                }}
                disabled={
                  data.read_only || !isEditing || forms[formIndex].isLocked
                }
                decimals={data.number_of_decimal_places}
                inputProps={{
                  min: data.min_value,
                  max: data.max_value,
                }}
              />
            </div>

            <FormFieldActions
              data={data.field_attach}
              location={location}
              formTemplates={formTemplates}
              handleKeepOldForm={handleKeepOldForm}
              isEditing={
                !data.read_only && isEditing && !forms[formIndex].isLocked
              }
              onChange={
                handleUpdateFormFieldAttachment
                  ? (value, type) =>
                    handleUpdateFormFieldAttachment(
                      value,
                      formIndex,
                      fieldIndex,
                      repeatableIndex,
                      repeatableFieldIndex,
                      type,
                    )
                  : null
              }
            />
          </div>
        );

      case "increment":
        return (
          <Increment
            value={data.value || data.min_value || 0}
            disabled={data.read_only || !isEditing || forms[formIndex].isLocked}
            min={data.min_value}
            max={data.max_value}
            onChange={(value) =>
              handleUpdateFormField(
                value,
                formIndex,
                fieldIndex,
                repeatableIndex,
                repeatableFieldIndex,
              )
            }
            inputProps={{
              step: 1,
            }}
          />
        );
      case "checkbox":
        if (data.type === "checkbox") {
          return (
            <div className="d-flex flex-column align-items-center w-100">
              <CheckboxLayout
                items={data.values.map((item) => ({
                  label: item,
                  checked:
                    data.value && data.value.length
                      ? data.value.includes(item)
                      : false,
                }))}
                disabled={
                  data.read_only || !isEditing || forms[formIndex].isLocked
                }
                isVertical={false}
                onChange={(items) =>
                  handleUpdateFormField(
                    items.map((item) => item.label),
                    formIndex,
                    fieldIndex,
                    repeatableIndex,
                    repeatableFieldIndex,
                  )
                }
              />

              <FormFieldActions
                data={data.field_attach}
                location={location}
                formTemplates={formTemplates}
                handleKeepOldForm={handleKeepOldForm}
                isEditing={
                  !data.read_only && isEditing && !forms[formIndex].isLocked
                }
                onChange={
                  handleUpdateFormFieldAttachment
                    ? (value, type) =>
                      handleUpdateFormFieldAttachment(
                        value,
                        formIndex,
                        fieldIndex,
                        repeatableIndex,
                        repeatableFieldIndex,
                        type,
                      )
                    : null
                }
              />
            </div>
          );
        } else if (data.type === "radio_button") {
          return (
            <div className="d-flex flex-column align-items-center w-100">
              <RadioLayout
                items={data.values.map((item) => ({
                  label: item,
                  checked: data.value && data.value === item,
                }))}
                name={
                  data.field_code +
                  (repeatableIndex && repeatableFieldIndex
                    ? `_${repeatableIndex}_${repeatableFieldIndex}`
                    : "")
                }
                onChange={(value) =>
                  handleUpdateFormField(
                    value,
                    formIndex,
                    fieldIndex,
                    repeatableIndex,
                    repeatableFieldIndex,
                  )
                }
                isVertical={false}
                disabled={
                  data.read_only || !isEditing || forms[formIndex].isLocked
                }
              />

              <FormFieldActions
                data={data.field_attach}
                location={location}
                formTemplates={formTemplates}
                handleKeepOldForm={handleKeepOldForm}
                isEditing={
                  !data.read_only && isEditing && !forms[formIndex].isLocked
                }
                onChange={
                  handleUpdateFormFieldAttachment
                    ? (value, type) =>
                      handleUpdateFormFieldAttachment(
                        value,
                        formIndex,
                        fieldIndex,
                        repeatableIndex,
                        repeatableFieldIndex,
                        type,
                      )
                    : null
                }
              />
            </div>
          );
        } else if (data.type === "button") {
          return (
            <div className="d-flex flex-column align-items-center w-100">
              <ButtonLayout
                items={data.values}
                name={data.field_code}
                checkedItem={data.value}
                onChecked={(value) =>
                  handleUpdateFormField(
                    value,
                    formIndex,
                    fieldIndex,
                    repeatableIndex,
                    repeatableFieldIndex,
                  )
                }
                disabled={
                  data.read_only || !isEditing || forms[formIndex].isLocked
                }
              />

              <FormFieldActions
                data={data.field_attach}
                location={location}
                formTemplates={formTemplates}
                handleKeepOldForm={handleKeepOldForm}
                isEditing={
                  !data.read_only && isEditing && !forms[formIndex].isLocked
                }
                onChange={
                  handleUpdateFormFieldAttachment
                    ? (value, type) =>
                      handleUpdateFormFieldAttachment(
                        value,
                        formIndex,
                        fieldIndex,
                        repeatableIndex,
                        repeatableFieldIndex,
                        type,
                      )
                    : null
                }
              />
            </div>
          );
        }
        break;
      case "selection":
        const isMulti = data.type === "multi_selection";
        const isCascadingSet = data.data_source === "from_cascading_set";

        const isFieldEnabled = isCascadingFieldEnabled({ forms, data, formIndex, repeatableIndex });

        const placeholderData = getDynamicPlaceholder({ forms, data, formIndex, repeatableIndex });
        const dynamicPlaceholder = placeholderData
          ? translate(placeholderData.key, { level: placeholderData.level }) || `Önce ${placeholderData.level} seçimi yapınız.`
          : "";

        let value =
          typeof data.value === "string" && data?.value
            ? { value: data.value, label: data.value }
            : data.value;

        if (isMulti) {
          value =
            data.value?.length > 0
              ? typeof data.value === "object"
                ? typeof data.value[0] === "string"
                  ? data.value.map((value) => ({ value, label: value }))
                  : data.value
                : typeof data.value === "string" && data.value.includes(",")
                  ? data.value
                    .split(",")
                    .map((value) => ({ value, label: value }))
                  : {
                    value: data.value,
                    label: data.value,
                  }
              : data.value || [];
        }

        const options = getCascadingOptions({ forms, data, formIndex, repeatableIndex });

        return (
          <div className="d-flex flex-column align-items-center w-100">
            <SelectBox
              options={options}
              onChange={(item) => {
                let newValue = item?.value ? item : "";

                if (isMulti)
                  newValue = item?.length
                    ? item.map(({ value, label }) => ({
                      value,
                      label,
                    }))
                    : [];

                if (isCascadingSet) {
                  handleCascadingChange(newValue, formIndex, fieldIndex, data, repeatableIndex, repeatableFieldIndex);
                } else {
                  handleUpdateFormField(
                    newValue,
                    formIndex,
                    fieldIndex,
                    repeatableIndex,
                    repeatableFieldIndex,
                  );
                }
              }}
              value={value}
              disabled={
                data.read_only ||
                !isEditing ||
                forms[formIndex].isLocked ||
                !isFieldEnabled
              }
              className="w-100"
              isClearable={true}
              isMulti={isMulti}
              placeholder={
                dynamicPlaceholder ||
                (!isFieldEnabled ? translate("forms.select_parent_first") || "Önce üst kategoriyi seçin" : "")
              }
            />

            <FormFieldActions
              data={data.field_attach}
              location={location}
              formTemplates={formTemplates}
              handleKeepOldForm={handleKeepOldForm}
              isEditing={
                !data.read_only && isEditing && !forms[formIndex].isLocked
              }
              onChange={
                handleUpdateFormFieldAttachment
                  ? (value, type) =>
                    handleUpdateFormFieldAttachment(
                      value,
                      formIndex,
                      fieldIndex,
                      repeatableIndex,
                      repeatableFieldIndex,
                      type,
                    )
                  : null
              }
            />
          </div>
        );
      case "staticDocument":
        return (
          <a
            href={data.file_obj?.file_path}
            className="btn btn-orange border rounded shadow-sm text-white"
            target="_blank"
            rel="noreferrer noopener"
          >
            {data.button_label || data.field_label}
          </a>
        );
      case "staticImage":
        return (
          <img
            src={data.image_obj?.file_path}
            alt="Statik Görsel"
            className="mw-100"
          />
        );
      case "textarea":
        return (
          <Textarea
            rows={5}
            value={data.value}
            disabled={data.read_only || !isEditing || forms[formIndex].isLocked}
            onChange={(e) =>
              handleUpdateFormField(
                e.target.value,
                formIndex,
                fieldIndex,
                repeatableIndex,
                repeatableFieldIndex,
              )
            }
          />
        );
      case "signature":
        return (
          <SignaturePad
            disabled={data.read_only || !isEditing || forms[formIndex].isLocked}
            onSave={(base64) =>
              handleUpdateFormField(
                base64,
                formIndex,
                fieldIndex,
                repeatableIndex,
                repeatableFieldIndex,
              )
            }
            value={data.value}
          />
        );
      case "rating":
        return (
          <div className="d-flex flex-column align-items-center w-100">
            <Rating
              rating={data.value}
              limit={data.max_value}
              onChange={(value) =>
                handleUpdateFormField(
                  value,
                  formIndex,
                  fieldIndex,
                  repeatableIndex,
                  repeatableFieldIndex,
                )
              }
              disabled={
                data.read_only || !isEditing || forms[formIndex].isLocked
              }
            />

            <FormFieldActions
              data={data.field_attach}
              location={location}
              formTemplates={formTemplates}
              handleKeepOldForm={handleKeepOldForm}
              isEditing={
                !data.read_only && isEditing && !forms[formIndex].isLocked
              }
              onChange={
                handleUpdateFormFieldAttachment
                  ? (value, type) =>
                    handleUpdateFormFieldAttachment(
                      value,
                      formIndex,
                      fieldIndex,
                      repeatableIndex,
                      repeatableFieldIndex,
                      type,
                    )
                  : null
              }
            />
          </div>
        );
      case "gps":
        if (data.view_options === "lat_long") {
          return (
            <LatLngMap
              lat={data.value?.lat || ""}
              lng={data.value?.lng || ""}
              read_only={data.read_only}
              required={data.required}
              hidden={data.hidden}
              onChange={({ lat, lng }) =>
                handleUpdateFormField(
                  { lat, lng },
                  formIndex,
                  fieldIndex,
                  repeatableIndex,
                  repeatableFieldIndex,
                )
              }
              userLocation={userLocation}
            />
          );
        } else if (
          data.view_options === "large_map" ||
          data.view_options === "small_map"
        ) {
          return (
            <GPSMap
              lat={data.value?.lat || 41.03323}
              lng={data.value?.lng || 29.18129}
              disabled={
                data.read_only || !isEditing || forms[formIndex].isLocked
              }
              required={data.required}
              type={data.view_options}
              hidden={data.hidden}
              handleUpdate={(lat, lng) =>
                handleUpdateFormField(
                  { lat, lng },
                  formIndex,
                  fieldIndex,
                  repeatableIndex,
                  repeatableFieldIndex,
                )
              }
              userLocation={userLocation}
            />
          );
        } else if (data.view_options === "map_link") {
          return (
            <Input
              type="url"
              value={data.url}
              onChange={(e) =>
                handleUpdateFormField(
                  e.target.value,
                  formIndex,
                  fieldIndex,
                  repeatableIndex,
                  repeatableFieldIndex,
                )
              }
              inputProps={{
                pattern: "^(https?://)?([da-z.-]+).([a-z.]{2,6})([/w .-]*)*/?$",
              }}
              disabled={
                data.read_only || !isEditing || forms[formIndex].isLocked
              }
            />
          );
        }
        break;
      case "slider":
        return (
          <Range
            min={data.min_value}
            max={data.max_value}
            value={data.value || ""}
            onChange={(value) =>
              handleUpdateFormField(
                value,
                formIndex,
                fieldIndex,
                repeatableIndex,
                repeatableFieldIndex,
              )
            }
            disabled={data.read_only || !isEditing || forms[formIndex].isLocked}
          />
        );
      case "date/time":
        return (
          <div className="d-flex flex-column align-items-center w-100">
            <DateTimeLayout
              type={data.type}
              maxValue={data.max_value}
              minValue={data.min_value}
              value={data.value}
              disabled={
                data.read_only || !isEditing || forms[formIndex].isLocked
              }
              onChange={(value) =>
                handleUpdateFormField(
                  value,
                  formIndex,
                  fieldIndex,
                  repeatableIndex,
                  repeatableFieldIndex,
                )
              }
              language={language}
            />

            <FormFieldActions
              data={data.field_attach}
              location={location}
              formTemplates={formTemplates}
              handleKeepOldForm={handleKeepOldForm}
              isEditing={
                !data.read_only && isEditing && !forms[formIndex].isLocked
              }
              onChange={
                handleUpdateFormFieldAttachment
                  ? (value, type) =>
                    handleUpdateFormFieldAttachment(
                      value,
                      formIndex,
                      fieldIndex,
                      repeatableIndex,
                      repeatableFieldIndex,
                      type,
                    )
                  : null
              }
            />
          </div>
        );
      case "image/photo":
        return (
          <Photos
            title="Photos"
            type={data.type}
            photos={data.value || []}
            disabled={data.read_only || !isEditing || forms[formIndex].isLocked}
            location={location}
            onUpdate={(photos) =>
              handleUpdateFormField(
                photos,
                formIndex,
                fieldIndex,
                repeatableIndex,
                repeatableFieldIndex,
              )
            }
            translate={translate}
          />
        );
      case "barcode":
        return (
          <Input
            type="text"
            value={data.value}
            onChange={(e) =>
              handleUpdateFormField(
                e.target.value,
                formIndex,
                fieldIndex,
                repeatableIndex,
                repeatableFieldIndex,
              )
            }
            disabled={data.read_only || !isEditing || forms[formIndex].isLocked}
          />
        );
      default:
        return <div>Component not found</div>;
    }
  };

  return forms
    .sort((a, b) => a.index - b.index)
    .map((form, index) => (
      <div key={form.index} className="row pb-3 mb-4 ps-5">
        <div className="col-12">
          <p className="fs-2 border-2 border-bottom mt-3">{form.name}</p>
        </div>

        {/** Components */}
        {form?.fields.map((component, index2) =>
          component.isRepeatable ? (
            <div
              key={index2}
              className="col-12 col-lg-10 mt-4 ms-0 ps-0 ps-lg-5 ms-lg-5 repeatable-group"
            >
              <div className="repeatable-area border border-1 rounded-2 shadow-sm p-3">
                {component.fields.map((repeatableFields, index3) => (
                  <div
                    className="d-flex flex-column align-items-start justify-content-center"
                    key={repeatableFields[0]?._uniqueId || index3}
                  >
                    {repeatableFields.map(
                      (repeatableComponent, repeatableIndex) => (
                        <div
                          key={`${index3}-${repeatableIndex}`}
                          className="row align-items-start mt-4 w-100"
                        >
                          {repeatableComponent.field_type !== "title" &&
                            <p className="col-12 col-lg-4 mb-0 fw-bold pt-3">
                              {repeatableComponent.field_label}
                            </p>}
                          <div className="d-flex justify-content-center col-12 col-lg-8">
                            {componentRender(
                              repeatableComponent,
                              index,
                              index2,
                              index3,
                              repeatableIndex,
                            )}
                          </div>
                        </div>
                      ),
                    )}
                    {isEditing && (
                      <div className="row mt-4 w-100">
                        <div className="col-12 d-flex justify-content-center align-items-center mb-2 add-button-col">
                          {index3 === component.fields.length - 1 && (
                            <div
                              className="add-button mx-1"
                              data-disabled={form?.isLocked}
                              style={{
                                opacity: form?.isLocked ? 0.3 : 1,
                                cursor: form?.isLocked ? "not-allowed" : "pointer",
                              }}
                              role="button"
                              onClick={() => {
                                if (form?.isLocked) return;
                                handleAddRemoveRepeatableForm(
                                  "add",
                                  index2,
                                  index,
                                  index3,
                                );
                              }
                              }
                            >
                              <AiOutlinePlusCircle className="fs-2 text-primary"/>
                            </div>
                          )}

                          {component.fields.length > 1 && (
                            <div
                              className="remove-button mx-1"
                              role="button"
                              data-disabled={form?.isLocked}
                              style={{
                                opacity: form?.isLocked ? 0.3 : 1,
                                cursor: form?.isLocked ? "not-allowed" : "pointer",
                              }}
                              onClick={() => {
                                if (form?.isLocked) return;
                                handleAddRemoveRepeatableForm(
                                  "remove",
                                  index2,
                                  index,
                                  index3,
                                );
                              }
                              }
                            >
                              <AiOutlineMinusCircle className="fs-2 text-danger"/>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {index3 !== component.fields.length - 1 && (
                      <div className="row mt-4">
                        <div className="col-12">
                          <div className="divider border-bottom"></div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              key={index2}
              className="col-12 col-lg-10 mt-4 ms-0 ps-0 ps-lg-5 ms-lg-5"
            >
              <div className="row align-items-start">
                {component.field_type !== "title" && (
                  <p className="col-12 col-lg-4 mb-0 fw-bold">
                    {component.field_label}
                  </p>
                )}
                <div className="col-12 col-lg-8">
                  {componentRender(component, index, index2)}
                </div>
              </div>
            </div>
          ),
        )}
        {/** Components */}
      </div>
    ));
};

export default Forms;
